import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useFormik } from 'formik';
import * as Yup from "yup";
import classnames from "classnames";
import { Modal, ModalHeader, ModalBody, ModalFooter, Form, Row, Col, Label, FormFeedback, Input, Button, Alert, Spinner } from "reactstrap";
import { createSelector } from 'reselect';
import { toggleCreateFeedModal, toggleAddFieldModal, toggleSelectPathModal } from '../../../slices/modals/reducer';
import CustomSelect from '../../../Components/Common/CustomSelect';
import { loadSource } from '../../../slices/feeds/thunk';
import { resetFeedState, setFeedErr, setPostFeedBody } from '../../../slices/feeds/reducer';

// const url = /^((http|https):\/\/)?(www.)?(?!.*(http|https|www.))[a-zA-Z0-9_-]+(\.[a-zA-Z]+)+(\/)?.([\w\?[a-zA-Z-_%\/@?]+)*([^\/\w\?[a-zA-Z0-9_-]+=\w+(&[a-zA-Z0-9_]+=\w+)*)?$/;
const url = "";

function CreateFeedModal() {
  const dispatch = useDispatch();
  const selectState = createSelector(state => state, state => ({
    createFeedModal: state.Modal.createFeedModal,
    mappedSource: state.Feed.mappedSource,
    feedErr: state.Feed.feedErr,
    feedErrMsg: state.Feed.feedErrMsg
  }));
  const { createFeedModal, mappedSource, feedErr, feedErrMsg } = useSelector(selectState);
  // Formik Validation
  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: "",
      type: "",
      source: "",
    },
    validationSchema: Yup.object({
      name: Yup.string().required("Please enter feed name"),
      type: Yup.object().shape({
        name: Yup.string().required("Please select source type"),
        value: Yup.string().required("Please select source type")
      }),
      source: Yup.string().matches(/^(http:\/\/|https:\/\/)/, 'The URL must start with http:// or https://')
      .url('Please enter a valid URL')
      .required('The source is required')
    }),
    onSubmit: async (values) => {
      // dispatch(resetFeedState());
      dispatch(setPostFeedBody(values));
      const isSourceLoaded = await dispatch(loadSource(validation.values.source));
      if (isSourceLoaded) {
        closeModal() // Close Create Feed Modal
        // dispatch(toggleSelectPathModal()) // Open Path Modal
        dispatch(toggleAddFieldModal());
      }
    }
  });

  function handleClick(key) {
    validation.setFieldTouched(key, true);
  };

  function handleChange(key, value) {
    handleClick(key);
    validation.setFieldValue(key, value)
  }

  const selectSourceLoadingState = createSelector((state) => state.Feed, (state) => state.sourceLoading);
  const sourceLoading = useSelector(selectSourceLoadingState);

  function closeModal() {
    dispatch(toggleCreateFeedModal());
    setTimeout(() => {
      validation.handleReset();
    }, 300)
  }

  useEffect(() => {
    if (feedErrMsg) {
      setTimeout(() => {
        dispatch(setFeedErr({feedErr: false, feedErrMsg: null}));
      }, 3000); 
    }
  }, [dispatch, feedErr])

  return (
    <Modal isOpen={createFeedModal} toggle={closeModal} centered size="md">
      <ModalHeader className='p-3' toggle={closeModal}>
        Create New Feed
      </ModalHeader>
      <Form className='tablelist-form'
        onSubmit={(e) => {
          e.preventDefault();
          validation.handleSubmit();
          return false;
        }}>
        <ModalBody className='px-3'>
          <Row>
            <Col>
              <p className='text-body-secondary'>
                Adding a new feed is easy. First you have to add the source to the content.
                It could be an item list of your store, a news feed to your blog or a table of
                sports odds. Then give the feed a name, like “My store inventory”.
              </p>
            </Col>
          </Row>
          <Row>
            <Col>
              {feedErr ? (<Alert className='mb-0 mt-4' color="danger"> {feedErrMsg} </Alert>) : null}
            </Col>
          </Row>
          <Row>
            <Col>
              <div className='mt-3'>
                <div className='w-100 d-flex justify-content-between'>
                  <Label htmlFor='name' className='form-label'>Feed Name</Label>
                  {validation.touched.name && !validation.errors.name ? (
                    <FormFeedback valid>Looks good</FormFeedback>
                  ) : null}
                  {validation.touched.name && validation.errors.name ? (
                    <FormFeedback type="invalid">{validation.errors.name}</FormFeedback>
                  ) : null}
                </div>
                <Input type='text' id='name' name='name'
                  placeholder='Ex. Dynamic Odds - Football - Premier League'
                  className={"form-control " + classnames({ "is-valid": validation.touched.name && !validation.errors.name })}
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  invalid={validation.touched.name && validation.errors.name ? true : false}
                  value={validation.values.name || ""}
                />
              </div>
            </Col>
          </Row>
          <Row>
            <div className='mt-3'>
              <div className='w-100 d-flex justify-content-between'>
                <Label htmlFor='type' className='form-label'>Source Type</Label>
                {validation.touched.type?.name && !validation.errors.type?.name ? (
                  <FormFeedback valid>Looks good</FormFeedback>
                ) : null}
                {validation.touched?.type?.name && validation.errors.type?.name ? (
                  <FormFeedback type="invalid">{validation.errors.type.name}</FormFeedback>
                ) : null}
              </div>
              <CustomSelect
                placeholder='All Text Versions'
                className='form-select'
                validation={classnames({
                  "is-valid": validation.touched.type?.name && !validation.errors.type?.name,
                  "is-invalid": validation.touched.type?.name && validation.errors.type?.name
                })}
                options={[{ name: "XML / JSON", value: "xml/json" }]}
                onChange={(item) => { handleChange("type", item) }}
                onClick={() => handleClick("type.name")}
              />
            </div>
          </Row>
          <Row>
            <div className='mt-3 mb-3'>
              <div className='w-100 d-flex justify-content-between'>
                <Label htmlFor='source' className='form-label'>Source</Label>
                {validation.touched.source && !validation.errors.source ? (
                  <FormFeedback valid>Looks good</FormFeedback>
                ) : null}
                {validation.touched.source && validation.errors.source ? (
                  <FormFeedback type="invalid">{validation.errors.source}</FormFeedback>
                ) : null}
              </div>
              <Input type='text' id='source' name='source'
                placeholder='ex. https://feed.adcropper.com/feed.xml'
                className={"form-control " + classnames({ "is-valid": validation.touched.source && !validation.errors.source })}
                onChange={validation.handleChange}
                onBlur={validation.handleBlur}
                invalid={validation.touched.source && validation.errors.source ? true : false}
                value={validation.values.source || ""}
              />
            </div>
          </Row>
        </ModalBody>
        <ModalFooter className='border-top pt-3 px-3'>
          <Button color="light" onClick={closeModal}>Close</Button>
          {(validation.touched.name && !validation.errors.name) &&
            (validation.touched.type && !validation.errors.type) &&
            (validation.touched.source && !validation.errors.source) ?
            <Button color="success" type='submit' disabled={(sourceLoading || feedErr) ? true : false}>
              Next: Select Path
              {(sourceLoading || feedErr) ? <Spinner size="sm" className="ms-2"></Spinner> : null}
            </Button>
            : null}
        </ModalFooter>
      </Form>
    </Modal>
  )
}

export default CreateFeedModal;